import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiShield, FiMapPin, FiArrowLeft } from 'react-icons/fi';

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseRing = keyframes`
  0% { transform: scale(0.8); opacity: 0.6; }
  70% { transform: scale(1.3); opacity: 0; }
  100% { transform: scale(0.8); opacity: 0; }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-18px); }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #0a174e 0%, #13245c 40%, #1a0a2e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 12s ease infinite;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 80px;
  direction: rtl;
  @media (max-width: 768px) {
    padding-top: 72px;
    min-height: 100svh;
  }
`;

const GlowOrb = styled.div<{ size: number; top: string; left?: string; right?: string; color: string; opacity?: number }>`
  position: absolute;
  width: ${p => p.size}px;
  height: ${p => p.size}px;
  top: ${p => p.top};
  left: ${p => p.left};
  right: ${p => p.right};
  background: ${p => p.color};
  border-radius: 50%;
  filter: blur(${p => p.size * 0.4}px);
  opacity: ${p => p.opacity ?? 0.18};
  pointer-events: none;
  z-index: 0;
`;

const GridPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  z-index: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 60px 40px 80px 40px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 60px;
  z-index: 2;
  position: relative;
  @media (max-width: 900px) {
    flex-direction: column;
    padding: 40px 20px 60px 20px;
    gap: 40px;
    text-align: center;
  }
  @media (max-width: 600px) {
    padding: 32px 16px 48px 16px;
    gap: 32px;
  }
`;

const TextSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 900px) {
    align-items: center;
    order: 2;
  }
`;

const LaunchBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(218, 1, 92, 0.15);
  border: 1px solid rgba(218, 1, 92, 0.4);
  border-radius: 100px;
  padding: 6px 16px;
  margin-bottom: 24px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #ff4d8f;
  letter-spacing: 0.04em;
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #DA015C;
  display: inline-block;
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 2px solid #DA015C;
    animation: ${pulseRing} 1.5s ease-out infinite;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
  direction: rtl;
  @media (max-width: 600px) {
    font-size: 2.2rem;
    margin-bottom: 1rem;
  }
`;

const Accent = styled.span`
  color: #DA015C;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.15rem;
  color: rgba(255,255,255,0.72);
  margin-bottom: 2.4rem;
  line-height: 1.8;
  direction: rtl;
  max-width: 520px;
  @media (max-width: 900px) {
    text-align: center;
    max-width: 500px;
  }
  @media (max-width: 600px) {
    font-size: 1.05rem;
    margin-bottom: 2rem;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  @media (max-width: 900px) {
    justify-content: center;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`;

const PrimaryButton = styled(motion.a)`
  background: #DA015C;
  color: #fff;
  padding: 0.95rem 2.2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: 2px solid #DA015C;
  white-space: nowrap;
  &:hover {
    background: #b8004e;
    border-color: #b8004e;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(218,1,92,0.4);
  }
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const SecondaryButton = styled(motion.a)`
  background: rgba(255,255,255,0.08);
  color: #fff;
  padding: 0.95rem 2.2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: 2px solid rgba(255,255,255,0.2);
  white-space: nowrap;
  backdrop-filter: blur(8px);
  &:hover {
    background: rgba(255,255,255,0.15);
    border-color: rgba(255,255,255,0.4);
    transform: translateY(-2px);
  }
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 32px;
  @media (max-width: 900px) { justify-content: center; }
  @media (max-width: 480px) { gap: 20px; }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 900px) { align-items: center; }
`;

const StatNumber = styled.span`
  font-size: 1.9rem;
  font-weight: 900;
  color: #fff;
  line-height: 1;
`;

const StatLabel = styled.span`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
`;

const Divider = styled.div`
  width: 1px;
  height: 44px;
  background: rgba(255,255,255,0.12);
  align-self: center;
`;

const ImageSide = styled.div`
  flex: 0 0 auto;
  width: 420px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  @media (max-width: 900px) {
    width: 100%;
    max-width: 360px;
    order: 1;
  }
  @media (max-width: 600px) { max-width: 280px; }
`;

const PhoneGlow = styled.div`
  position: absolute;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(218,1,92,0.3) 0%, transparent 70%);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 0;
`;

const PhoneImage = styled(motion.img)`
  width: 100%;
  max-width: 340px;
  height: auto;
  object-fit: contain;
  position: relative;
  z-index: 2;
  border-radius: 28px;
  border: 3px solid rgba(255,255,255,0.12);
  box-shadow: 0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06);
  animation: ${floatY} 5s ease-in-out infinite;
  @media (max-width: 600px) { max-width: 240px; }
`;

const FloatingChip = styled(motion.div)`
  position: absolute;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  z-index: 4;
  white-space: nowrap;
`;

const ChipIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0a174e, #13245c);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.95rem;
  flex-shrink: 0;
`;

const ChipText = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #0a174e;
  line-height: 1.3;
  direction: rtl;
`;

const HeroHe: React.FC = () => (
  <HeroSection id="home">
    <GridPattern />
    <GlowOrb size={500} top="-15%" right="-10%" color="#DA015C" opacity={0.12} />
    <GlowOrb size={400} top="40%" left="10%" color="#3b5bdb" opacity={0.15} />

    <ContentWrapper>
      <TextSide>
        <LaunchBadge
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <PulseDot /> מגיעים בקרוב לעיר שלך
        </LaunchBadge>

        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          נסיעות שמקדמות <br />
          <Accent>קהילות שזקוקות לשינוי</Accent>
        </Title>

        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          וואסל היא פלטפורמת ההסעות שנבנתה עבור קהילות שמגיעות להן נסיעות בטוחות, משתלמות ואמינות — בקרוב בעיר שלך.
        </Subtitle>

        <ButtonsWrapper>
          <PrimaryButton
            href="#waitlist"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={e => {
              e.preventDefault();
              document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <FiArrowLeft /> הצטרף כנוסע
          </PrimaryButton>
          <SecondaryButton
            href="#waitlist"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={e => {
              e.preventDefault();
              document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            הפוך לנהג
          </SecondaryButton>
        </ButtonsWrapper>

        <StatsRow>
          <Stat>
            <StatNumber>24/7</StatNumber>
            <StatLabel>תמיד זמין</StatLabel>
          </Stat>
          <Divider />
          <Stat>
            <StatNumber>100%</StatNumber>
            <StatLabel>נהגים מאומתים</StatLabel>
          </Stat>
          <Divider />
          <Stat>
            <StatNumber>₪0</StatNumber>
            <StatLabel>עמלות נסתרות</StatLabel>
          </Stat>
        </StatsRow>
      </TextSide>

      <ImageSide>
        <PhoneGlow />
        <PhoneImage
          src="/images/wassel-app-screen.png"
          alt="אפליקציית וואסל"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        />
        <FloatingChip
          style={{ top: '14%', left: '-12%' }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <ChipIcon><FiShield /></ChipIcon>
          <ChipText>נהגים<br />מבודקים</ChipText>
        </FloatingChip>
        <FloatingChip
          style={{ bottom: '18%', right: '-14%' }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }}
        >
          <ChipIcon><FiMapPin /></ChipIcon>
          <ChipText>מעקב GPS<br />בזמן אמת</ChipText>
        </FloatingChip>
      </ImageSide>
    </ContentWrapper>
  </HeroSection>
);

export default HeroHe;
